<template>
  <section id="ai-chat">
    <h1>AI 开店助手</h1>
    <p class="ai-chat-description">有任何关于包材定制、材质选择、包装搭配或开店初期包装规划的问题，都可以通过 AI 助手先进行初步咨询。我们希望用更简单的方式，帮助你快速整理需求与方向。</p>
    
    <div class="ai-chat-container">
      <div class="chat-header">
        <div class="ai-status">
          <div class="status-indicator"></div>
          <span>KPP智能助手</span>
        </div>
        <div class="chat-actions">
          <i class="fas fa-volume-up" :class="{ active: voiceEnabled }" title="语音播报" @click="toggleVoice"></i>
          <button id="reset-button" title="重置对话" @click="resetConversation">
            <i class="fas fa-redo"></i>
          </button>
        </div>
      </div>
      
      <div class="chat-messages" ref="chatMessages">
        <div v-for="(message, index) in messages" :key="index"
             :class="['message', `${message.sender}-message`, {'with-title': message.hasTitle}]">
          <div class="message-text">
            <p v-for="(line, i) in formatMessageText(message.text)" :key="i" v-html="line"></p>
          </div>
          <div class="message-time" v-if="message.sender !== 'typing'">{{ message.time }}</div>
        </div>
      </div>
      
      <div class="suggestion-chips">
        <div class="suggestion-chip" 
             v-for="(chip, index) in suggestionChips" 
             :key="index"
             @click="useChipSuggestion(chip)">
          {{ chip }}
        </div>
      </div>
      
      <div class="chat-input-container">
        <input type="text" class="chat-input" v-model="userInput" 
               placeholder="输入您的问题..." autocomplete="off"
               @keypress.enter="sendMessage" :disabled="inputDisabled">
        <button class="send-button" @click="sendMessage" :disabled="inputDisabled">
          <i class="fas fa-paper-plane"></i>
        </button>
      </div>
    </div>
  </section>
  <div class="divider"></div>
</template>

<script>
import axios from 'axios';
import apiBaseUrl from '@/config/api.js';

// 24 小时会话超时（毫秒），超过则下次请求前清空 session，由后端生成新 session_id
const SESSION_TIMEOUT_MS = 24 * 60 * 60 * 1000;
const STORAGE_KEY_SESSION_ID = 'kpp_session_id';
const STORAGE_KEY_LAST_ACTIVITY = 'kpp_session_last_activity_at';

export default {
  name: 'AiChatView',
  data() {
    return {
      messages: [],
      userInput: '',
      inputDisabled: false,
      voiceEnabled: false,
      currentQuestionIndex: 0,
      conversationStep: "start",
      suggestionChips: [
        "如何选择包装材料？",
        "环保包装有哪些选择？",
        "定制包装的流程是什么？",
        "包装设计需要多长时间？",
        "如何提高品牌辨识度？"
      ],
      apiBaseUrl,
      showTypingIndicator: false,
      serverStatus: 'checking' // 'checking', 'connected', 'disconnected'
    }
  },
  mounted() {
    console.log('[AiChatView] 组件已挂载');
    this.logMessage('AI聊天视图已挂载，准备初始化对话');
    
    // 检查服务器状态
    this.checkServerStatus();
    
    // 打印当前状态
    this.logMessage(`初始化状态: conversationStep=${this.conversationStep}, currentQuestionIndex=${this.currentQuestionIndex}`);
  },
  methods: {
    // 检查服务器状态
    async checkServerStatus() {
      this.logMessage('正在检查服务器状态...');
      
      try {
        const response = await axios.get(`${this.apiBaseUrl}/api/health`, { timeout: 3000 });
        
        if (response.status === 200) {
          this.serverStatus = 'connected';
          this.logMessage('服务器连接成功，状态正常');
          
          // 服务器连接成功后开始对话
          this.startConversation();
        } else {
          this.serverStatus = 'disconnected';
          this.logMessage(`服务器返回非200状态码: ${response.status}`);
          
          // 显示离线消息
          this.addMessage('无法连接到AI服务器，将以离线模式运行，功能受限。', 'ai');
          this.conversationStep = "freeChat";
        }
      } catch (error) {
        this.serverStatus = 'disconnected';
        this.logMessage(`服务器连接失败: ${error.message}`);
        
        // 尝试开始对话，但可能会处于离线模式
        this.startConversation();
      }
    },
    
    // 格式化消息文本，支持换行和特殊标记
    formatMessageText(text) {
      // 增加日志
      this.logMessage(`格式化消息文本: ${text.substring(0, 30)}...`);
      
      // 将**text**格式转换为粗体
      const boldPattern = /\*\*(.*?)\*\*/g;
      const lines = text.split('\n').filter(line => line.trim());
      
      this.logMessage(`消息分行数: ${lines.length}`);
      
      return lines.map((line, index) => {
        // 替换加粗标记为HTML标签
        if (line.match(boldPattern)) {
          this.logMessage(`为第${index+1}行添加加粗样式`);
          return line.replace(boldPattern, '<strong>$1</strong>');
        }
        // 高亮选项标记 (A. B. C. 等)
        if (line.match(/^[A-F]\.\s/)) {
          this.logMessage(`为第${index+1}行添加选项样式`);
          return `<span class="option-marker">${line}</span>`;
        }
        return line;
      });
    },
    
    // 日志记录
    logMessage(message) {
      const timestamp = new Date().toLocaleTimeString();
      console.log(`[AI开店助手 ${timestamp}] ${message}`);
    },
    
    // 添加消息到聊天界面
    addMessage(text, sender) {
      this.logMessage(`添加${sender}消息: ${text.substring(0, 50)}${text.length > 50 ? '...' : ''}`);
      
      const now = new Date();
      const time = `${now.getHours()}:${now.getMinutes().toString().padStart(2, '0')}`;
      
      // 检查是否含有问题标题
      const hasTitle = text.includes('**问题') || text.match(/^问题\d+/);
      
      if (hasTitle) {
        this.logMessage('消息包含问题标题，应用特殊样式');
      }
      
      this.messages.push({
        text,
        sender,
        time,
        hasTitle
      });
      
      // 使用nextTick确保DOM更新后滚动到底部
      this.$nextTick(() => {
        if (this.$refs.chatMessages) {
          const chatMessages = this.$refs.chatMessages;
          chatMessages.scrollTop = chatMessages.scrollHeight;
          this.logMessage('滚动消息框到底部');
        }
      });
      
      // 如果启用了语音播报并且是AI消息，使用语音合成
      if (this.voiceEnabled && sender === 'ai' && window.speechSynthesis) {
        this.logMessage('启动语音播报');
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = 'zh-CN';
        window.speechSynthesis.speak(utterance);
      }
    },
    
    // 显示正在输入指示器
    showTyping() {
      this.logMessage('显示正在输入指示器');
      this.showTypingIndicator = true;
      const typingMessage = {
        text: 'AI正在思考...',
        sender: 'typing',
        time: ''
      };
      this.messages.push(typingMessage);
      
      // 滚动到底部
      this.$nextTick(() => {
        if (this.$refs.chatMessages) {
          const chatMessages = this.$refs.chatMessages;
          chatMessages.scrollTop = chatMessages.scrollHeight;
        }
      });
      
      return typingMessage;
    },
    
    // 隐藏正在输入指示器
    hideTyping(typingMessage) {
      this.logMessage('隐藏正在输入指示器');
      const index = this.messages.indexOf(typingMessage);
      if (index !== -1) {
        this.messages.splice(index, 1);
      }
      this.showTypingIndicator = false;
    },
    
    // 检查消息是否包含联系方式
    checkIfContainsContact(message) {
      console.log("检查消息是否包含联系方式");
      // 简单的正则表达式来检测电话号码和邮箱
      const phoneRegex = /1[3-9]\d{9}/g; // 中国手机号格式
      const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
      
      const hasPhone = phoneRegex.test(message);
      const hasEmail = emailRegex.test(message);
      
      return hasPhone || hasEmail;
    },

    /**
     * 24 小时会话超时切分：在每次发 /api/chat 请求前调用。
     * 若已有 session_id 且（无 last_activity_at 或距上次活动超过 24 小时），则清空本地 session，
     * 让本次请求不带 session_id，由后端生成新 session_id。
     */
    maybeClearExpiredSession() {
      const sessionId = localStorage.getItem(STORAGE_KEY_SESSION_ID);
      if (!sessionId) return;
      const lastAt = localStorage.getItem(STORAGE_KEY_LAST_ACTIVITY);
      if (!lastAt) {
        this.logMessage('[session] 无 last_activity_at，清空旧 session_id');
        localStorage.removeItem(STORAGE_KEY_SESSION_ID);
        localStorage.removeItem(STORAGE_KEY_LAST_ACTIVITY);
        return;
      }
      const lastMs = new Date(lastAt).getTime();
      if (isNaN(lastMs)) {
        this.logMessage('[session] last_activity_at 无效，清空旧 session_id');
        localStorage.removeItem(STORAGE_KEY_SESSION_ID);
        localStorage.removeItem(STORAGE_KEY_LAST_ACTIVITY);
        return;
      }
      const nowMs = Date.now();
      if (nowMs - lastMs > SESSION_TIMEOUT_MS) {
        this.logMessage('[session] 距上次活动已超过 24 小时，清空 session_id 以开启新会话');
        localStorage.removeItem(STORAGE_KEY_SESSION_ID);
        localStorage.removeItem(STORAGE_KEY_LAST_ACTIVITY);
      }
    },

    /** 成功收到 /api/chat 响应后调用，更新 last_activity_at */
    touchSessionActivity() {
      localStorage.setItem(STORAGE_KEY_LAST_ACTIVITY, new Date().toISOString());
    },
    
    // 发送消息到服务器
    async sendMessage() {
      const userMessage = this.userInput.trim();
      if (!userMessage) {
        this.logMessage('用户消息为空，不发送');
        return;
      }

      this.logMessage(`用户发送消息: ${userMessage}`);
      this.addMessage(userMessage, 'user');
      this.userInput = "";
      this.inputDisabled = true;

      try {
        this.logMessage(`发送请求到后端，conversationStep: ${this.conversationStep}, currentQuestionIndex: ${this.currentQuestionIndex}`);
        
        // leaveContact 不短路：请求交给后端保存并返回感谢语 + freeChat，由下方统一处理 response
        // 显示正在输入的指示器
        const typingIndicator = this.showTyping();
        
        try {
          this.maybeClearExpiredSession();
          this.logMessage(`发送HTTP请求到: ${this.apiBaseUrl}/api/chat`);
          const chatBody = {
            message: userMessage,
            conversationStep: this.conversationStep,
            currentQuestionIndex: this.currentQuestionIndex,
            userInput: userMessage,
            session_id: localStorage.getItem('kpp_session_id') || undefined,
            lead_id: localStorage.getItem('kpp_lead_id') || undefined
          };
          const response = await axios.post(`${this.apiBaseUrl}/api/chat`, chatBody);
          
          this.logMessage(`收到服务器响应: ${JSON.stringify(response.data).substring(0, 100)}...`);
          if (response.data.session_id) {
            localStorage.setItem('kpp_session_id', response.data.session_id);
            this.touchSessionActivity();
          }
          if (response.data.lead_id != null && response.data.lead_id !== '') {
            localStorage.setItem('kpp_lead_id', String(response.data.lead_id));
          }
          // 隐藏输入指示器
          this.hideTyping(typingIndicator);
          
          // 更新对话状态
          this.conversationStep = response.data.conversationStep;
          this.currentQuestionIndex = response.data.currentQuestionIndex;
          localStorage.setItem('conversationStep', this.conversationStep);
          localStorage.setItem('currentQuestionIndex', this.currentQuestionIndex.toString());

          // 题目只通过 nextQuestion 渲染；response/extraMessages 只放回答/补充/确认，不编号
          if (response.data.response) {
            this.addMessage(response.data.response, 'ai');
          }
          if (Array.isArray(response.data.extraMessages)) {
            response.data.extraMessages.forEach(text => {
              if (text && typeof text === 'string') this.addMessage(text, 'ai');
            });
          }
          if (response.data.nextQuestion) {
            this.logMessage('从 nextQuestion 显示题目气泡');
            setTimeout(() => {
              const questionNumber = this.currentQuestionIndex + 1;
              const formattedOptions = (response.data.nextQuestion.options || [])
                .map((opt, index) => `${String.fromCharCode(65 + index)}. ${opt}`)
                .join('\n');
              const nextQuestionText = `**问题${questionNumber}**\n${response.data.nextQuestion.question}\n\n${formattedOptions}`;
              this.addMessage(nextQuestionText, 'ai');
            }, 1000);
          }
          
          // 如果进入了自由问答模式
          if (this.conversationStep === "freeChat") {
            this.logMessage(`已进入自由问答模式，等待用户输入`);
          }
          
          // 如果还在处理AI响应，需要发送另一个请求
          if (this.conversationStep === "processingAI") {
            this.logMessage(`进入AI处理状态，等待1秒后发送请求`);
            
            // 等待一秒后发送请求
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            // 显示正在输入指示器
            const aiTypingIndicator = this.showTyping();
            
            try {
              this.maybeClearExpiredSession();
              this.logMessage('发送AI处理请求');
              const aiChatBody = {
                message: userMessage,
                conversationStep: "processingAI",
                currentQuestionIndex: this.currentQuestionIndex,
                userInput: userMessage,
                session_id: localStorage.getItem('kpp_session_id') || undefined,
                lead_id: localStorage.getItem('kpp_lead_id') || undefined
              };
              const aiResponse = await axios.post(`${this.apiBaseUrl}/api/chat`, aiChatBody);
              
              this.logMessage(`收到AI处理响应: ${JSON.stringify(aiResponse.data).substring(0, 100)}...`);
              if (aiResponse.data.session_id) {
                localStorage.setItem('kpp_session_id', aiResponse.data.session_id);
                this.touchSessionActivity();
              }
              if (aiResponse.data.lead_id != null && aiResponse.data.lead_id !== '') localStorage.setItem('kpp_lead_id', String(aiResponse.data.lead_id));
              // 隐藏输入指示器
              this.hideTyping(aiTypingIndicator);
              
              // 显示AI回复
              this.addMessage(aiResponse.data.response, 'ai');
              
              // 更新对话状态
              const prevAiStep = this.conversationStep;
              const prevAiIndex = this.currentQuestionIndex;
              
              this.conversationStep = aiResponse.data.conversationStep;
              this.currentQuestionIndex = aiResponse.data.currentQuestionIndex;
              
              this.logMessage(`AI处理后状态更新: ${prevAiStep} -> ${this.conversationStep}, 问题索引: ${prevAiIndex} -> ${this.currentQuestionIndex}`);
              
              // 更新本地存储
              localStorage.setItem('conversationStep', this.conversationStep);
              localStorage.setItem('currentQuestionIndex', this.currentQuestionIndex.toString());
              
              // 如果有下一个问题，延迟显示
              if (aiResponse.data.nextQuestion) {
                this.logMessage('AI处理后有下一个问题，准备显示');
                setTimeout(() => {
                  const questionNumber = this.currentQuestionIndex + 1;
                  this.logMessage(`显示问题${questionNumber}`);
                  
                  const formattedOptions = aiResponse.data.nextQuestion.options.map((opt, index) => 
                    `${String.fromCharCode(65 + index)}. ${opt}`).join('\n');
                  const nextQuestionText = `**问题${questionNumber}**\n${aiResponse.data.nextQuestion.question}\n\n${formattedOptions}`;
                  this.addMessage(nextQuestionText, 'ai');
                }, 1000);
              }
            } catch (error) {
              console.error("AI处理请求失败:", error);
              this.logMessage(`AI处理请求失败: ${error.message}`);
              
              // 隐藏输入指示器
              this.hideTyping(aiTypingIndicator);
              
              this.addMessage("抱歉，连接AI服务器失败，请检查网络连接或稍后再试。", 'ai');
            }
          }
        } catch (error) {
          console.error("请求失败:", error);
          this.logMessage(`请求失败: ${error.message}`);
          
          // 隐藏输入指示器
          this.hideTyping(typingIndicator);
          
          this.addMessage(`抱歉，连接服务器失败，请确保后端服务已启动。错误信息：${error.message}`, 'ai');
        } finally {
          this.inputDisabled = false;
          this.logMessage('输入框已解锁，可以继续输入');
        }
      } catch (error) {
        console.error("处理消息失败:", error);
        this.logMessage(`处理消息失败: ${error.message}`);
        this.addMessage("抱歉，系统繁忙，请稍后再试。", 'ai');
        this.inputDisabled = false;
      }
    },
    
    // 开始对话
    async startConversation() {
      this.logMessage(`开始对话初始化`);
      
      // 清空聊天窗口
      this.messages = [];
      
      // 添加初始消息
      this.addMessage("正在连接AI助手，请稍候...", 'ai');
      
      try {
        // 首次进入页面必须从 start/welcome 开始，不使用 localStorage 恢复的 step/index，避免发出 waitingForChoice + 空 message
        localStorage.setItem('conversationStep', 'start');
        localStorage.setItem('currentQuestionIndex', '0');
        this.logMessage('初始化请求固定使用 conversationStep=start, currentQuestionIndex=0');

        const typingIndicator = this.showTyping();
        try {
          this.maybeClearExpiredSession();
          this.logMessage('发送初始化请求');
          const initBody = {
            message: "",
            conversationStep: "start",
            currentQuestionIndex: 0,
            session_id: localStorage.getItem('kpp_session_id') || undefined,
            lead_id: localStorage.getItem('kpp_lead_id') || undefined
          };
          const response = await axios.post(`${this.apiBaseUrl}/api/chat`, initBody);
          
          this.logMessage(`初始化成功，收到响应: ${JSON.stringify(response.data).substring(0, 100)}...`);
          if (response.data.session_id) {
            localStorage.setItem('kpp_session_id', response.data.session_id);
            this.touchSessionActivity();
          }
          if (response.data.lead_id != null && response.data.lead_id !== '') {
            localStorage.setItem('kpp_lead_id', String(response.data.lead_id));
          }
          this.hideTyping(typingIndicator);

          // 题目只通过 nextQuestion 渲染；response 为欢迎语等，不按题目解析
          if (response.data.response) {
            this.addMessage(response.data.response, 'ai');
          }
          if (Array.isArray(response.data.extraMessages)) {
            response.data.extraMessages.forEach(text => {
              if (text && typeof text === 'string') this.addMessage(text, 'ai');
            });
          }
          if (response.data.nextQuestion) {
            const questionNumber = (response.data.currentQuestionIndex ?? 0) + 1;
            const formattedOptions = (response.data.nextQuestion.options || [])
              .map((opt, index) => `${String.fromCharCode(65 + index)}. ${opt}`)
              .join('\n');
            const nextQuestionText = `**问题${questionNumber}**\n${response.data.nextQuestion.question}\n\n${formattedOptions}`;
            this.addMessage(nextQuestionText, 'ai');
          }

          this.conversationStep = response.data.conversationStep;
          this.currentQuestionIndex = response.data.currentQuestionIndex;
          localStorage.setItem('conversationStep', this.conversationStep);
          localStorage.setItem('currentQuestionIndex', this.currentQuestionIndex.toString());
        } catch (error) {
          console.error("初始化请求失败:", error);
          this.logMessage(`初始化请求失败: ${error.message}`);
          
          // 隐藏输入指示器
          this.hideTyping(typingIndicator);
          
          // 显示错误信息
          this.addMessage('连接服务器失败，请确保后端服务已启动。', 'ai');
          this.addMessage("您可以在无服务器模式下进行基本对话，但AI功能将不可用。", 'ai');
          
          // 进入自由问答模式（无服务器）
          this.conversationStep = "freeChat";
          this.currentQuestionIndex = -1;
          this.logMessage('进入离线自由问答模式');
        }
      } catch (error) {
        console.error("初始化对话失败:", error);
        this.logMessage(`初始化对话失败: ${error.message}`);
        
        // 显示错误信息
        this.addMessage("连接服务器失败，请确保服务器已启动。", 'ai');
        this.addMessage("您可以在无服务器模式下进行基本对话，但AI功能将不可用。", 'ai');
        
        // 进入自由问答模式（无服务器）
        this.conversationStep = "freeChat";
        this.currentQuestionIndex = -1;
        this.logMessage('进入离线自由问答模式');
      } finally {
        this.inputDisabled = false;
      }
    },
    
    // 重置对话
    async resetConversation() {
      this.logMessage(`重置对话`);
      
      // 清空聊天窗口
      this.messages = [];
      
      // 添加初始消息
      this.addMessage("正在重置对话...", 'ai');
      
      try {
        // 调用重置API
        this.logMessage('发送重置请求到服务器');
        await axios.post(`${this.apiBaseUrl}/api/reset`, {});
        this.logMessage(`重置API调用成功`);
        
        // 重置状态
        this.currentQuestionIndex = 0;
        this.conversationStep = "start";
        this.logMessage('重置本地状态');
        
        // 清除本地存储
        localStorage.removeItem('conversationStep');
        localStorage.removeItem('currentQuestionIndex');
        this.logMessage('清除本地存储的对话状态');
        
        // 重新开始对话
        this.startConversation();
      } catch (error) {
        console.error("重置对话失败:", error);
        this.logMessage(`重置对话失败: ${error.message}`);
        
        // 显示错误信息
        this.addMessage("重置对话失败，请刷新页面重试。", 'ai');
        
        // 尝试本地重置
        this.currentQuestionIndex = 0;
        this.conversationStep = "start";
        this.logMessage('执行本地重置');
        
        // 清除本地存储
        localStorage.removeItem('conversationStep');
        localStorage.removeItem('currentQuestionIndex');
        
        // 重新开始对话
        setTimeout(() => {
          this.logMessage('尝试本地重新初始化对话');
          this.startConversation();
        }, 1000);
      }
    },
    
    // 切换语音播报
    toggleVoice() {
      this.voiceEnabled = !this.voiceEnabled;
      this.logMessage(`语音播报: ${this.voiceEnabled ? '开启' : '关闭'}`);
      
      if (!this.voiceEnabled && window.speechSynthesis) {
        window.speechSynthesis.cancel(); // 停止当前语音
        this.logMessage('停止当前播放的语音');
      }
    },
    
    // 使用建议问题
    useChipSuggestion(chip) {
      this.logMessage(`用户选择了建议问题: ${chip}`);
      this.userInput = chip;
      this.sendMessage();
    }
  }
}
</script>

<style scoped>
section {
  min-height: auto;
  padding: 20px 0 60px 0; /* 与 ContactView 保持一致的高度，上方 20px，下方 60px */
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  text-align: center;
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
}

.divider {
  width: 80%;
  max-width: 1200px;
  height: 2px;
  background: #e50914;
  margin: 0 auto;
}

/* AI聊天界面样式 */
.ai-chat-container {
  width: 95%;  /* 增加到95% */
  max-width: 1100px;  /* 进一步增加最大宽度 */
  height: 78vh;  /* 增加高度 */
  background: #141414;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 6px 25px rgba(0, 0, 0, 0.4);  /* 增强阴影效果 */
  display: flex;
  flex-direction: column;
  margin: 0 auto;
}

.chat-header {
  background: #111;
  color: white;
  padding: 10px 12px;  /* 减小padding */
  font-size: 14px;  /* 减小字体 */
  font-weight: bold;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #333;
}

.chat-header .ai-status {
  display: flex;
  align-items: center;
}

.status-indicator {
  width: 10px;
  height: 10px;
  background: #4CAF50;
  border-radius: 50%;
  margin-right: 8px;
  animation: pulse 2s infinite;
}

.chat-actions {
  display: flex;
  align-items: center;
}

#toggle-voice {
  cursor: pointer;
  margin-right: 15px;
}

#reset-button {
  background: transparent;
  border: 1px solid white;
  color: white;
  padding: 5px 10px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
}

@keyframes pulse {
  0% { opacity: 0.6; }
  50% { opacity: 1; }
  100% { opacity: 0.6; }
}

.chat-messages {
  flex-grow: 1;
  padding: 15px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 10px;  /* 调整消息间距 */
  background: #1a1a1a;  /* 微调背景色 */
  text-align: left;  /* AI 助手回复气泡内文字靠左对齐 */
}

.message {
  padding: 8px 10px;  /* 进一步缩小内边距 */
  border-radius: 12px;
  font-size: 1rem;  /* 与标题下方介绍文案一致的字号 */
  line-height: 1.4;
  position: relative;
}

.message-text {
  font-size: 1rem;  /* 与标题下方介绍文案一致的字号 */
  line-height: 1.4;
  white-space: pre-wrap;
  word-break: break-word;
  text-align: left;  /* 气泡内文字靠左 */
}

.message-text p {
  margin: 0 0 3px 0;  /* 缩小段落间距 */
  text-align: left;
}

.message-text p strong {
  font-weight: bold;
  font-size: 13px;  /* 缩小标题字体 */
  color: #e50914;
  display: block;
  margin-bottom: 3px;  /* 缩小标题下方间距 */
}

.message-text p .option-marker {
  display: block;
  padding: 3px 6px;  /* 缩小padding */
  margin: 3px 0;
  background: #222;
  border-left: 3px solid #e50914;
  font-weight: 500;
  white-space: normal;
  word-break: break-word;
}

.user-message {
  align-self: flex-end;
  background: #e50914;
  color: white;
  border-bottom-right-radius: 4px;
  max-width: 65%;  /* 调整最大宽度 */
  font-size: 1rem;  /* 与介绍文案保持一致 */
}

.ai-message {
  align-self: flex-start;
  background: #333;
  color: white;
  border-bottom-left-radius: 4px;
  max-width: 75%;  /* 调整最大宽度 */
  width: auto;
  font-size: 1rem;  /* 与介绍文案保持一致 */
}

.typing-message {
  align-self: flex-start;
  background: #333;
  color: white;
  border-bottom-left-radius: 4px;
  animation: pulse 1.3s infinite;
  max-width: 60%;
  font-size: 1rem;  /* 与介绍文案保持一致 */
}

.message-time {
  font-size: 9px;  /* 进一步缩小时间字体 */
  opacity: 0.7;
  margin-top: 3px;  /* 缩小上边距 */
  text-align: right;
}

.suggestion-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 6px 10px 8px;
  background: #111;
  flex-shrink: 0;           /* 固定高度不随对话增多被压缩 */
  min-height: 52px;         /* 适度高度，多留空间给上方对话区 */
  align-content: center;
}

.suggestion-chip {
  background: #333;
  color: white;
  padding: 6px 10px;
  border-radius: 12px;
  font-size: 12px;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.2s;
  line-height: 1.2;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 26px;
}

.suggestion-chip:hover {
  background: #444;
}

.chat-input-container {
  padding: 12px;  /* 减小padding */
  background: #111;
  display: flex;
  align-items: center;
  border-top: 1px solid #333;
}

.chat-input {
  flex-grow: 1;
  padding: 10px 12px;  /* 减小padding */
  background: #2a2a2a;
  color: white;
  border: none;
  border-radius: 20px;
  font-size: 14px;  /* 减小字体 */
  outline: none;
}

.chat-input:focus {
  box-shadow: 0 0 0 2px rgba(229, 9, 20, 0.3);
}

.send-button {
  background: #e50914;
  color: white;
  border: none;
  width: 36px;  /* 减小尺寸 */
  height: 36px;  /* 减小尺寸 */
  border-radius: 50%;
  margin-left: 8px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: background 0.2s;
}

.send-button:hover {
  background: #f52234;
}

.send-button:disabled {
  background: #666;
  cursor: not-allowed;
}

/* 打字动画样式 */
.typing-indicator span {
  display: inline-block;
  animation: pulse 1.3s infinite;
}

.typing-indicator span:after {
  content: '...';
  animation: ellipsis 1.3s infinite;
}

@keyframes ellipsis {
  0% { content: '.'; }
  33% { content: '..'; }
  66% { content: '...'; }
}

/* 添加的样式 */
#reset-button {
  background: transparent;
  border: 1px solid white;
  color: white;
  padding: 4px 8px;  /* 减小padding */
  border-radius: 4px;
  cursor: pointer;
  font-size: 11px;  /* 减小字体 */
  margin-left: 8px;
}

#toggle-voice.active {
  color: #e50914;
}

/* 添加响应式设计 */
@media (max-width: 768px) {
  .ai-chat-container {
    width: 98%;  /* 进一步增加宽度 */
    height: 85vh;  /* 进一步增加高度 */
    margin: 0 auto;
  }
  
  .chat-messages {
    padding: 10px;  /* 缩小内边距 */
  }
  
  .message {
    font-size: 0.95rem;  /* 移动端略小一号，保证可读性 */
    padding: 7px 9px;  /* 缩小内边距 */
    line-height: 1.2;
  }
  
  .message-text {
    font-size: 0.95rem;  /* 与 .message 保持一致 */
  }
  
  .message-text p strong {
    font-size: 12px;  /* 缩小标题字体 */
  }
  
  .suggestion-chips {
    padding: 5px 5px 6px;
    min-height: 46px;
  }
  
  .suggestion-chip {
    padding: 5px 8px;
    font-size: 11px;
    min-height: 24px;
  }
  
  .chat-input {
    font-size: 11px;  /* 缩小输入框字体 */
    padding: 8px 10px;  /* 缩小内边距 */
  }
}

/* 对具有标题的消息进行特殊处理 */
.ai-message.with-title {
  width: auto;
  max-width: 75%;
}

#ai-chat h1 {
  font-size: 1.8rem;  /* 减小标题字体 */
  margin-bottom: 0.8rem;
  color: white;
}

.ai-chat-description {
  font-size: 1rem;
  margin-bottom: 1.5rem;
  margin-left: auto;
  margin-right: auto;
  max-width: 728px;  /* 标题下方介绍文字限宽，阅读更宽松 */
  line-height: 1.7;
  color: #ccc;
  text-align: center;
}

/* 确保 AI 回复与连接中提示等气泡内文字一律靠左，不受上方介绍样式影响 */
.ai-message .message-text,
.ai-message .message-text p,
.typing-message .message-text,
.typing-message .message-text p {
  text-align: left;
}
</style> 